import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, CheckCircle, Printer, IndianRupee } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { generateId, formatCurrency, formatDate } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';

const emptyForm = {
  // Personal
  firstName: '', lastName: '', dob: '', gender: 'boy', bloodGroup: 'A+',
  phone: '', email: '', address: '', city: '', district: '', pinCode: '', state: '',
  // Identity
  category: 'General', caste: '', religion: '', aadharNumber: '', penNumber: '',
  // Academic
  classId: '', sectionId: 'A', rollNumber: '',
  admissionDate: new Date().toISOString().split('T')[0],
  admissionNumber: '', status: 'active', feeGroup: '', feeStructureId: '',
  // Father
  fatherName: '', fatherOccupation: '', fatherPhone: '',
  // Mother
  motherName: '', motherOccupation: '', motherPhone: '',
  // Guardian
  guardianType: 'father', guardianName: '', guardianRelation: '', guardianPhone: '', guardianOccupation: '',
  // Parent link
  parentId: '',
  // Transport
  transportRouteId: '', transportStop: '',
};

const emptyAdmPay = { mode: 'cash', ref: '', confirmed: false };

function Field({ label, children, span = 1 }) {
  const spanClass = span === 4 ? 'col-span-4' : span === 3 ? 'col-span-4' : span === 2 ? 'col-span-2' : '';
  return (
    <div className={spanClass}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionCard({ number, title, children, cols = 3 }) {
  const gridClass = cols === 4 ? 'grid-cols-4' : 'grid-cols-3';
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
        <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">{number}</span>
        {title}
      </h2>
      <div className={`grid ${gridClass} gap-4`}>
        {children}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300';

async function printAdmissionReceipt({ payId, studentName, admissionNumber, className, amount, mode, ref, date }) {
  const school = await getSchoolInfo();
  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const win = window.open('', '_blank', 'width=700,height=500');
  win.document.open();
  win.document.write(`
    <html><head><title>Admission Fee Receipt - ${studentName}</title>
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Roboto', Arial, sans-serif; padding: 32px 40px; color: #111; font-size: 13px; }
      .header { margin-bottom: 16px; }
      .badge { display: inline-block; background: #fef3c7; color: #92400e; border: 1px solid #fde68a; padding: 3px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; margin-top: 6px; }
      .receipt-no { text-align: right; font-size: 12px; color: #555; margin-bottom: 16px; }
      .receipt-no strong { font-size: 14px; color: #111; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      td { padding: 9px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
      td:first-child { color: #555; width: 40%; }
      td:last-child { font-weight: 500; }
      .amount-row td { font-size: 16px; font-weight: 700; border-top: 2px solid #e5e7eb; border-bottom: 2px solid #e5e7eb; }
      .amount-row td:last-child { color: #6b21a8; }
      .footer { text-align: center; font-size: 11px; color: #aaa; margin-top: 28px; padding-top: 10px; border-top: 1px dashed #e5e7eb; }
      .print-btn { text-align: center; margin-top: 20px; }
      @media print { .print-btn { display: none; } body { padding: 20px; } * { color:#000 !important; } }
    </style></head><body>
    <div class="header">
      ${schoolHeader(school, '#6b21a8', 'Admission Fee Receipt')}
      <div style="text-align:center;margin-top:-4px;margin-bottom:10px"><span class="badge">One-Time Payment</span></div>
    </div>
    <div class="receipt-no">Receipt No: <strong>${payId}</strong> &nbsp;&nbsp; Date: <strong>${formatDate(date)}</strong></div>
    <table>
      <tr><td>Student Name</td><td>${studentName}</td></tr>
      <tr><td>Admission Number</td><td>${admissionNumber}</td></tr>
      <tr><td>Class</td><td>${className || '—'}</td></tr>
      <tr><td>Payment Mode</td><td style="text-transform:capitalize">${mode}</td></tr>
      ${ref ? `<tr><td>Transaction Ref.</td><td>${ref}</td></tr>` : ''}
      <tr class="amount-row"><td>Admission Fee Paid</td><td>${fmt(amount)}</td></tr>
    </table>
    <div class="footer">Computer-generated receipt &mdash; ${school.name}</div>
    <div class="print-btn">
      <button onclick="window.print()" style="background:#6b21a8;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer;font-family:'Roboto',Arial,sans-serif">Print Receipt</button>
    </div>
    </body></html>
  `);
  win.document.close();
}

function NewAdmission() {
  const { classes, parents, feeStructures, routes, saveStudent, saveFeePayment, admissionFee, academicYear, feeGroups } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...emptyForm, admissionNumber: `ADM${Date.now()}` });
  const [saved, setSaved] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [admPay, setAdmPay] = useState(emptyAdmPay);
  const [submitting, setSubmitting] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const feeRequired = Number(admissionFee) > 0;
  const canSubmit = !feeRequired || admPay.confirmed;

  const confirmPayment = () => {
    if (!admPay.mode) return;
    setAdmPay(p => ({ ...p, confirmed: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const studentId = generateId('STU');
    const ok = await saveStudent({ ...form, id: studentId });
    if (ok) {
      let payId = null;
      if (feeRequired && admPay.confirmed) {
        // fee_structures.class_id stores the class NAME (e.g. "Class 8"), not
        // a class row id — see FeeStructure.js — so match on name, not id.
        const className = classes.find(c => c.id === form.classId)?.name;
        const fs = feeStructures.find(f => f.classId === className || f.classId === form.classId);
        payId = generateId('PAY');
        await saveFeePayment({
          id: payId,
          studentId,
          feeStructureId: fs?.id || '',
          classId: form.classId,
          academicYear: academicYear || fs?.academicYear || '',
          type: 'admissionFee',
          amount: Number(admissionFee),
          discount: 0,
          fine: 0,
          paymentMode: admPay.mode,
          transactionRef: admPay.ref,
          paidDate: form.admissionDate || new Date().toISOString().split('T')[0],
          collectedBy: 'admission',
          feeLabel: 'Admission Fee',
          status: 'paid',
        });
      }
      const cls = classes.find(c => c.id === form.classId);
      setSavedData({
        name: `${form.firstName} ${form.lastName}`,
        admissionNumber: form.admissionNumber,
        className: cls ? `${cls.name} - ${cls.section}` : '',
        payId,
        date: form.admissionDate || new Date().toISOString().split('T')[0],
      });
      setSaved(true);
      // Auto-print receipt if admission fee was collected
      if (payId) {
        printAdmissionReceipt({
          payId,
          studentName: `${form.firstName} ${form.lastName}`,
          admissionNumber: form.admissionNumber,
          className: cls ? `${cls.name} - ${cls.section}` : '',
          amount: Number(admissionFee),
          mode: admPay.mode,
          ref: admPay.ref,
          date: form.admissionDate || new Date().toISOString().split('T')[0],
        });
      }
    }
    setSubmitting(false);
  };

  const handleAnother = () => {
    setForm({ ...emptyForm, admissionNumber: `ADM${Date.now()}` });
    setAdmPay(emptyAdmPay);
    setSaved(false);
    setSavedData(null);
  };

  if (saved && savedData) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-bold mb-1">Admission Successful</h2>
          <p className="text-gray-500 text-sm mb-1">{savedData.name} has been admitted.</p>
          <p className="text-gray-400 text-xs mb-6">Adm No: {savedData.admissionNumber} &nbsp;&middot;&nbsp; {savedData.className}</p>
          {savedData.payId && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs text-amber-600 font-medium">Admission Fee Collected</p>
                <p className="text-sm font-bold text-amber-800">{formatCurrency(admissionFee)}</p>
                <p className="text-xs text-amber-600">Receipt: {savedData.payId}</p>
              </div>
              <button
                onClick={() => printAdmissionReceipt({
                  payId: savedData.payId,
                  studentName: savedData.name,
                  admissionNumber: savedData.admissionNumber,
                  className: savedData.className,
                  amount: Number(admissionFee),
                  mode: admPay.mode,
                  ref: admPay.ref,
                  date: savedData.date,
                })}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700"
              >
                <Printer size={13} /> Reprint
              </button>
            </div>
          )}
          <div className="flex justify-center gap-3">
            <button onClick={handleAnother} className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
              New Admission
            </button>
            <button onClick={() => navigate('/students')} className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">
              View All Students
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">New Admission</h1>
        <p className="text-gray-500 text-sm mt-0.5">Register a new student into the school</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* 1. Personal Information */}
        <SectionCard number="1" title="Personal Information" cols={4}>
          <Field label="First Name">
            <input required value={form.firstName} onChange={e => set('firstName', e.target.value)} className={inputCls} placeholder="e.g. Arjun" />
          </Field>
          <Field label="Last Name">
            <input required value={form.lastName} onChange={e => set('lastName', e.target.value)} className={inputCls} placeholder="e.g. Kumar" />
          </Field>
          <Field label="Date of Birth">
            <DateInput required value={form.dob} onChange={e => set('dob', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Gender">
            <select value={form.gender} onChange={e => set('gender', e.target.value)} className={inputCls}>
              {['boy','girl'].map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Blood Group">
            <select value={form.bloodGroup} onChange={e => set('bloodGroup', e.target.value)} className={inputCls}>
              {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
          </Field>
          <Field label="Email" span={2}>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} placeholder="student@email.com" />
          </Field>
          <Field label="Address (House / Street)" span={4}>
            <input value={form.address} onChange={e => set('address', e.target.value)} className={inputCls} placeholder="House no., street, locality" />
          </Field>
          <Field label="City / Town">
            <input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} placeholder="e.g. Hyderabad" />
          </Field>
          <Field label="District">
            <input value={form.district} onChange={e => set('district', e.target.value)} className={inputCls} placeholder="e.g. Rangareddy" />
          </Field>
          <Field label="PIN Code">
            <input value={form.pinCode} onChange={e => set('pinCode', e.target.value)} className={inputCls} placeholder="e.g. 500001" maxLength={6} />
          </Field>
          <Field label="State">
            <select value={form.state} onChange={e => set('state', e.target.value)} className={inputCls}>
              <option value="">Select State</option>
              {['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Andaman and Nicobar Islands','Chandigarh','Dadra & Nagar Haveli and Daman & Diu','Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          {/* Father */}
          <div className="col-span-4 border-t border-gray-100 pt-2 mt-1"><p className="font-semibold">Father's Details</p></div>
          <Field label="Father's Name">
            <input value={form.fatherName} onChange={e => set('fatherName', e.target.value)} className={inputCls} placeholder="Full name" />
          </Field>
          <Field label="Father's Occupation">
            <input value={form.fatherOccupation} onChange={e => set('fatherOccupation', e.target.value)} className={inputCls} placeholder="e.g. Engineer" />
          </Field>
          <Field label="Father's Phone">
            <input type="tel" value={form.fatherPhone} onChange={e => set('fatherPhone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
          </Field>
          {/* Mother */}
          <div className="col-span-4 border-t border-gray-100 pt-2 mt-1"><p className="font-semibold">Mother's Details</p></div>
          <Field label="Mother's Name">
            <input value={form.motherName} onChange={e => set('motherName', e.target.value)} className={inputCls} placeholder="Full name" />
          </Field>
          <Field label="Mother's Occupation">
            <input value={form.motherOccupation} onChange={e => set('motherOccupation', e.target.value)} className={inputCls} placeholder="e.g. Teacher" />
          </Field>
          <Field label="Mother's Phone">
            <input type="tel" value={form.motherPhone} onChange={e => set('motherPhone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
          </Field>
          {/* Guardian */}
          <div className="col-span-4 border-t border-gray-100 pt-2 mt-1"><p className="font-semibold">Guardian / Emergency Contact</p></div>
          <Field label="Guardian Is" span={4}>
            <div className="flex gap-2">
              {[['father', 'Father'], ['mother', 'Mother'], ['other', 'Other (Custom)']].map(([val, label]) => (
                <button key={val} type="button"
                  onClick={() => set('guardianType', val)}
                  className={`px-4 py-1 rounded-lg text-sm font-medium border transition-colors ${form.guardianType === val ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
            </div>
          </Field>
          {form.guardianType === 'other' ? (
            <>
              <Field label="Guardian Name">
                <input value={form.guardianName} onChange={e => set('guardianName', e.target.value)} className={inputCls} placeholder="Full name" />
              </Field>
              <Field label="Relation">
                <input value={form.guardianRelation} onChange={e => set('guardianRelation', e.target.value)} className={inputCls} placeholder="e.g. Uncle, Grandparent" />
              </Field>
              <Field label="Guardian Phone">
                <input type="tel" value={form.guardianPhone} onChange={e => set('guardianPhone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
              </Field>
              <Field label="Guardian Occupation" span={2}>
                <input value={form.guardianOccupation} onChange={e => set('guardianOccupation', e.target.value)} className={inputCls} placeholder="e.g. Business" />
              </Field>
            </>
          ) : (
            <div className="col-span-4 bg-gray-50 rounded-lg px-4 py-1 text-sm">
              {form.guardianType === 'father' ? (
                form.fatherName
                  ? <span><strong>{form.fatherName}</strong> — {form.fatherOccupation || 'Occupation not filled'} &nbsp;|&nbsp; {form.fatherPhone || 'Phone not filled'}</span>
                  : <span className="text-gray-400">Fill Father's details above to auto-populate.</span>
              ) : (
                form.motherName
                  ? <span><strong>{form.motherName}</strong> — {form.motherOccupation || 'Occupation not filled'} &nbsp;|&nbsp; {form.motherPhone || 'Phone not filled'}</span>
                  : <span className="text-gray-400">Fill Mother's details above to auto-populate.</span>
              )}
            </div>
          )}
        </SectionCard>

        {/* 2. Identity & Community */}
        <SectionCard number="2" title="Identity & Community">
          <Field label="Category">
            <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls}>
              {['General','OBC','SC','ST','EWS','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Caste">
            <input value={form.caste} onChange={e => set('caste', e.target.value)} className={inputCls} placeholder="e.g. Brahmin" />
          </Field>
          <Field label="Religion">
            <select value={form.religion} onChange={e => set('religion', e.target.value)} className={inputCls}>
              <option value="">Select Religion</option>
              {['Hindu','Muslim','Christian','Sikh','Buddhist','Jain','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Aadhar Number">
            <input value={form.aadharNumber} onChange={e => set('aadharNumber', e.target.value)} className={inputCls} placeholder="12-digit Aadhar number" maxLength={14} />
          </Field>
          <Field label="PEN Number">
            <input value={form.penNumber} onChange={e => set('penNumber', e.target.value)} className={inputCls} placeholder="Permanent Education Number" />
          </Field>
        </SectionCard>

        {/* 3. Academic Details */}
        <SectionCard number="3" title="Academic Details" cols={4}>
          <Field label="Class">
            <select required value={form.classId} onChange={e => {
              const cid = e.target.value;
              const className = classes.find(c => c.id === cid)?.name;
              const fs = feeStructures.find(f => f.classId === className || f.classId === cid);
              setForm(f => ({ ...f, classId: cid, feeStructureId: fs?.id || '' }));
            }} className={inputCls}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} — Section {c.section}</option>)}
            </select>
          </Field>
          <Field label="Section">
            <select value={form.sectionId} onChange={e => set('sectionId', e.target.value)} className={inputCls}>
              {['A','B','C','D'].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Roll Number">
            <input value={form.rollNumber} onChange={e => set('rollNumber', e.target.value)} className={inputCls} placeholder="e.g. 42" />
          </Field>
          <Field label="Admission Number">
            <input value={form.admissionNumber} onChange={e => set('admissionNumber', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date of Admission">
            <DateInput value={form.admissionDate} onChange={e => set('admissionDate', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Fee Group">
            <select value={form.feeGroup} onChange={e => set('feeGroup', e.target.value)} className={inputCls}>
              <option value="">Select Fee Group</option>
              {feeGroups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Fee Structure" span={2}>
            <select value={form.feeStructureId} onChange={e => set('feeStructureId', e.target.value)} className={inputCls}>
              <option value="">Select Fee Structure</option>
              {(() => {
                const className = classes.find(c => c.id === form.classId)?.name;
                let list = form.classId
                  ? feeStructures.filter(fs => fs.classId === className || fs.classId === form.classId)
                  : feeStructures;
                if (form.feeStructureId && !list.find(fs => fs.id === form.feeStructureId)) {
                  const current = feeStructures.find(fs => fs.id === form.feeStructureId);
                  if (current) list = [current, ...list];
                }
                return list.map(fs => (
                  <option key={fs.id} value={fs.id}>{fs.name} — ₹{fs.totalAmount?.toLocaleString('en-IN')} ({fs.frequency})</option>
                ));
              })()}
            </select>
            {form.classId && feeStructures.filter(fs => {
              const className = classes.find(c => c.id === form.classId)?.name;
              return fs.classId === className || fs.classId === form.classId;
            }).length === 0 && (
              <p className="text-orange-500 text-xs mt-1">No fee structure defined for this class yet.</p>
            )}
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls}>
              {['active','inactive','transferred','graduated'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </Field>
        </SectionCard>

        {/* 4. Transport (optional) */}
        <SectionCard number="4" title="Transport (Optional)">
          {(() => {
            const selectedRoute = routes.find(r => r.id === form.transportRouteId);
            const stops = selectedRoute?.stops || [];
            const selectedStop = stops.find(s => s.name === form.transportStop);
            return (<>
              <Field label="Route">
                <select value={form.transportRouteId} onChange={e => set('transportRouteId', e.target.value)} className={inputCls}>
                  <option value="">— No Transport —</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name || r.routeName} ({r.startPoint} → {r.endPoint})</option>)}
                </select>
              </Field>
              <Field label="Stop">
                <select value={form.transportStop} onChange={e => set('transportStop', e.target.value)} className={inputCls} disabled={!form.transportRouteId}>
                  <option value="">— Select Stop —</option>
                  {stops.map(s => <option key={s.name} value={s.name}>{s.name}{s.fee ? ` — ₹${Number(s.fee).toLocaleString('en-IN')}` : ''}</option>)}
                </select>
              </Field>
              <Field label="Annual Transport Fee">
                <div className="px-3 py-1 border border-gray-200 rounded-lg text-sm bg-gray-50 font-semibold text-indigo-700">
                  {selectedStop?.fee ? `₹${Number(selectedStop.fee).toLocaleString('en-IN')}` : '—'}
                </div>
              </Field>
            </>);
          })()}
        </SectionCard>

        {/* 5. Admission Fee — mandatory if configured */}
        {feeRequired && (
          <div className={`rounded-xl border-2 p-6 ${admPay.confirmed ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
            <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${admPay.confirmed ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}`}>5</span>
              <IndianRupee size={16} className={admPay.confirmed ? 'text-green-700' : 'text-amber-700'} />
              Admission Fee
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">One-time · Required before admission</span>
              <span className="ml-auto text-xl font-bold text-amber-800">{formatCurrency(admissionFee)}</span>
            </h2>

            {admPay.confirmed ? (
              <div className="flex items-center gap-3 bg-green-100 border border-green-300 rounded-xl px-5 py-4">
                <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Payment Confirmed — {formatCurrency(admissionFee)}</p>
                  <p className="text-sm text-green-700 capitalize">{admPay.mode}{admPay.ref ? ` · Ref: ${admPay.ref}` : ''}</p>
                </div>
                <button type="button" onClick={() => setAdmPay(p => ({ ...p, confirmed: false }))}
                  className="ml-auto text-xs text-green-600 underline hover:text-green-800">
                  Edit
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-amber-800">Collect the admission fee from the parent before proceeding. A receipt will be printed automatically after admission.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-amber-900">Amount</label>
                    <div className="px-3 py-1 border border-amber-300 rounded-lg text-sm bg-white font-bold text-amber-800">
                      {formatCurrency(admissionFee)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-amber-900">Payment Mode</label>
                    <select value={admPay.mode} onChange={e => setAdmPay(p => ({ ...p, mode: e.target.value }))}
                      className="w-full px-3 py-1 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white">
                      {['cash','cheque','online','UPI','DD'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-amber-900">Transaction Ref. (optional)</label>
                    <input value={admPay.ref} onChange={e => setAdmPay(p => ({ ...p, ref: e.target.value }))}
                      className="w-full px-3 py-1 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                      placeholder="Cheque / UPI ref." />
                  </div>
                </div>
                <button type="button" onClick={confirmPayment}
                  className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold transition-colors">
                  <CheckCircle size={15} /> Confirm Payment Received
                </button>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-6">
          <button type="button" onClick={() => navigate('/students')} className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            title={!canSubmit ? 'Confirm admission fee payment first' : ''}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${canSubmit ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            <UserPlus size={16} />
            {submitting ? 'Saving...' : 'Admit Student'}
            {!canSubmit && <span className="text-xs">(Confirm admission fee first)</span>}
          </button>
        </div>

      </form>
    </div>
  );
}
export default NewAdmission;
